import { QueryClient, type Query } from '@tanstack/react-query';

import { onHealthRecovered } from '@/lib/refreshHealth';
import { isReconnectingError } from '@/services/errors';

/** Retries of a query answered SESSION_RECONNECTING before its error shows. */
const RECONNECTING_MAX_RETRIES = 20;
/** Fallback poll while reconnecting; recovery normally refetches sooner. */
const RECONNECTING_RETRY_DELAY_MS = 30_000;

/** TanStack's own default backoff, kept for every other error. */
const defaultRetryDelay = (failureCount: number): number =>
  Math.min(1000 * 2 ** failureCount, 30_000);

/**
 * Shared TanStack Query client for apps/web.
 *
 * Defaults tuned for Joshua's workload:
 * - Business data changes infrequently within a session; 5 min staleTime
 *   keeps navigation snappy without serving stale reads across sessions.
 * - Window focus refetches are disabled because most Joshua workflows are
 *   long-running forms where refocusing would clobber user input.
 * - Two retries balance transient network failures against surfacing real
 *   errors quickly to the user.
 * - JOSH-89: a query answered SESSION_RECONNECTING keeps retrying (slowly)
 *   instead of failing, so it keeps its last data and never enters the error
 *   state; when health returns to ok it is refetched at once (below).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        isReconnectingError(error)
          ? failureCount < RECONNECTING_MAX_RETRIES
          : failureCount < 2,
      retryDelay: (failureCount, error) =>
        isReconnectingError(error) ? RECONNECTING_RETRY_DELAY_MS : defaultRetryDelay(failureCount),
    },
    mutations: {
      retry: 0,
    },
  },
});

function failedReconnecting(query: Query): boolean {
  return isReconnectingError(query.state.fetchFailureReason) || isReconnectingError(query.state.error);
}

/**
 * Refetches, once, every query whose last attempt was answered
 * SESSION_RECONNECTING. A query waiting out its retry delay is cancelled first
 * (reverting to its pre-fetch state, data kept), so it does not sleep on.
 */
export async function refetchReconnectingQueries(client: QueryClient = queryClient): Promise<void> {
  const targets = new Set(client.getQueryCache().findAll({ predicate: failedReconnecting }));
  if (targets.size === 0) return;
  await Promise.all([...targets].map((query) => query.cancel({ revert: true })));
  await client.refetchQueries({ type: 'active', predicate: (query) => targets.has(query) });
}

onHealthRecovered(() => {
  void refetchReconnectingQueries();
});
